import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { demosList } from "../data/demos";
import { SectionHead } from "./Services";
import { useLanguage } from "../context/LanguageContext";

export default function DemoCards() {
  const { t, dir, lang } = useLanguage();
  return (
    <section
      id="demos"
      dir={dir}
      lang={lang}
      className="relative py-24 md:py-32 px-5 sm:px-8"
      data-testid="demos-section"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHead
          eyebrow={t.demoCards.eyebrow}
          title={t.demoCards.title}
          sub={t.demoCards.sub}
        />

        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {demosList.map((d, i) => {
            const td = t.demosList[d.slug];
            return (
              <DemoCard
                key={d.slug}
                demo={{ ...d, type: td?.type ?? d.type, description: td?.description ?? d.description }}
                index={i}
                viewDemoLabel={t.demoCards.viewDemo}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DemoCard({ demo, index, viewDemoLabel }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [0, 1], [8, -8]), {
    stiffness: 180,
    damping: 14,
  });
  const ry = useSpring(useTransform(mx, [0, 1], [-8, 8]), {
    stiffness: 180,
    damping: 14,
  });

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      style={{ perspective: 900 }}
    >
      <Link
        to={demo.path}
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="block group relative overflow-hidden rounded-2xl border border-white/10 bg-agency-gray hover:border-agency-gold/40 transition-colors h-full"
        data-testid={`demo-card-${demo.slug}`}
      >
        <motion.div
          style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
          className="will-change-transform h-full flex flex-col"
        >
          <div className="relative aspect-video overflow-hidden">
            <img
              src={demo.cover}
              alt={demo.title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover object-center scale-[1.02] group-hover:scale-110 transition-transform duration-[1200ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <span
              className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.25em] font-bold backdrop-blur-md bg-white/10 border border-white/15"
              style={{ color: demo.accent }}
            >
              {demo.type}
            </span>
            <span className="absolute top-4 right-4 inline-flex items-center justify-center h-10 w-10 rounded-full bg-black/60 border border-white/10 group-hover:bg-agency-gold group-hover:text-black transition-colors">
              <ArrowUpRight size={18} />
            </span>
          </div>

          <div className="p-8 flex-1 flex flex-col justify-between">
            <div>
              <h3 className="font-display text-3xl font-bold tracking-tight">
                {demo.title}
              </h3>
              <p className="mt-3 text-base text-white/55 leading-relaxed">
                {demo.description}
              </p>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-agency-gold font-semibold">
              {viewDemoLabel}
              <span className="h-px w-8 bg-agency-gold transition-all group-hover:w-12" />
            </div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
}
